import { useMemo } from 'react';
import {
  LocationFeatureResponse,
  LocationsFeaturesResponse,
} from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { VOICE_AIR_CUSTOMIZATION_FLAGS } from '@frontend/shared';

type UseVoiceAiOptions = {
  locationIds: string[];
  enabled?: boolean;
};

type UseVoiceAiResult = {
  isLoading: boolean;
  activeLocationIds: string[];
};

const normalizeFlagName = (name?: string) => (name ?? '').toLowerCase().replace(/\s+/g, '');

const buildLocationFlagMapFromBatched = (
  data: LocationsFeaturesResponse | undefined,
  allowedIds: string[]
): Record<string, LocationFeatureResponse | undefined> => {
  if (!data) return {};

  const byLocation = data.locationsFeatures ?? {};
  const allowedSet = new Set(allowedIds);
  const flagMap: Record<string, LocationFeatureResponse | undefined> = {};

  Object.entries(byLocation).forEach(([locationId, flagsData]) => {
    if (!allowedSet.has(locationId)) return;

    const features = flagsData?.features ?? [];
    const match = features.find((f) => {
      const normalizedName = normalizeFlagName(f?.name);
      return VOICE_AIR_CUSTOMIZATION_FLAGS.includes(normalizedName as 'truelarkaivoice' | 'aivoice');
    });

    if (match) {
      flagMap[locationId] = match;
    }
  });

  return flagMap;
};

const getActiveLocationIds = (flagMap: Record<string, LocationFeatureResponse | undefined>): string[] => {
  return Object.entries(flagMap)
    .filter(([, value]) => {
      const state = value?.state;
      return (
        !!state &&
        [
          CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
          CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
        ].includes(state)
      );
    })
    .map(([locationId]) => locationId);
};

export const useVoiceAiLocations = (options: UseVoiceAiOptions): UseVoiceAiResult => {
  const { locationIds, enabled = true } = options;

  const shouldFetchFlags = enabled && locationIds.length > 0;

  const { data, isLoading } = CustomizationFlagQueries.useBatchedMultiCustomizationFlags(locationIds, {
    enabled: shouldFetchFlags,
  });

  const activeLocationIds = useMemo(() => {
    if (!shouldFetchFlags || !data) return [];
    const flagMap = buildLocationFlagMapFromBatched(data, locationIds);
    return getActiveLocationIds(flagMap);
  }, [data, locationIds, shouldFetchFlags]);

  return {
    isLoading,
    activeLocationIds,
  };
};
